import * as yup from "yup";

export interface SalaryFormContext {
  availableToPay: number;
}

const requiredId = (message: string) =>
  yup
    .number()
    .transform((value, originalValue) =>
      originalValue === "" || originalValue === null ? undefined : value,
    )
    .typeError(message)
    .moreThan(0, message)
    .required(message);

const optionalAmount = () =>
  yup
    .number()
    .transform((value, originalValue) =>
      originalValue === "" || originalValue === null ? 0 : value,
    )
    .typeError(" ")
    .min(0);

export const salaryPaymentLineSchema = yup.object({
  payment_method_id: yup
    .number()
    .transform((value, originalValue) =>
      originalValue === "" || originalValue === null ? null : value,
    )
    .nullable(),
  amount: optionalAmount(),
});

export const salaryFormSchema = yup.object({
  branch_id: requiredId("required"),
  employee_id: requiredId("required"),
  additional_pay: optionalAmount(),
  deduction: optionalAmount(),
  payment_date: yup.date().nullable(),
  received_date: yup.date().required("required").typeError("required"),
  remind_me_at: yup.date().nullable(),
  note: yup.string().nullable(),
  payments: yup
    .array()
    .of(salaryPaymentLineSchema)
    .test("methods-when-amount", "paymentMethodRequired", (lines) =>
      (lines ?? []).every(
        (l) => !(Number(l?.amount) > 0) || !!l?.payment_method_id,
      ),
    )
    .test("total-within-available", "payAmountExceedsRemaining", function (
      lines,
    ) {
      const available =
        (this.options.context as SalaryFormContext | undefined)
          ?.availableToPay ?? 0;
      const total = (lines ?? []).reduce(
        (sum, l) => sum + (Number(l?.amount) > 0 ? Number(l?.amount) : 0),
        0,
      );
      return total <= available + 1e-9;
    }),
});

export type SalaryFormValues = yup.InferType<typeof salaryFormSchema>;

export const salaryFormDefaultValues: SalaryFormValues = {
  branch_id: undefined as unknown as number,
  employee_id: undefined as unknown as number,
  additional_pay: 0,
  deduction: 0,
  payment_date: null,
  received_date: undefined as unknown as Date,
  remind_me_at: null,
  note: "",
  payments: [{ payment_method_id: null, amount: 0 }],
};
